import { isBillUploadEnabled } from './uiVisibility';
import { reportingTabHref } from './reportingTabs';
import { complianceSectionHref } from './complianceHub';
import { SUBSCRIPTION_PLANS_ENABLED } from './subscriptionPlans';
import { myCompanyNavPages } from './workspacePages';
import { CARBON_SAVINGS_TRADING_SECTIONS } from './carbonSavingsTrading';

export interface NavSubPage {
  label: string;
  /** Route path; append `?tab=N` when the page uses tab query params. */
  path: string;
}

/** Single disclosure reports entry; framework tabs live on the reporting page. */
function complianceReportingNavChildren(): NavSubPage[] {
  return [{ label: 'Disclosure reports', path: reportingTabHref('overview') }];
}

export interface NavTopic {
  label: string;
  path: string;
  children?: NavSubPage[];
}

export const SIDEBAR_WIDTH = 260;

/** Stable nav labels — update filterNavTopicsByProgress and resolveNavTopicIcon when renaming. */
export const NAV_TOPIC_LABELS = {
  home: 'Home',
  myCompany: 'My company',
  data: 'Data',
  emissions: 'Carbon footprint',
  compliance: 'Compliance',
  emissionsTargets: 'Reduce and offset',
} as const;

/**
 * MSME primary workspace navigation (left sidebar).
 * Linear journey: profile → bills → footprint → one report goal.
 * Operations mapping and accounting connectors stay visible under My company / Data
 * once a profile exists (not gated behind a hidden Advanced accordion).
 * Compliance precedes optimization modules so disclosure readiness
 * surfaces before marketplace/target workflows.
 * @see docs/root/MSME_WORKSPACE_NAVIGATION.md
 */
const UPLOAD_BILLS_NAV_CHILD: NavSubPage = { label: 'Upload bills', path: '/document-upload' };

export const PRIMARY_NAV_TOPICS: NavTopic[] = [
  { label: NAV_TOPIC_LABELS.home, path: '/dashboard' },
  {
    label: NAV_TOPIC_LABELS.myCompany,
    path: '/msme-profile',
    children: myCompanyNavPages().map((page) => ({ label: page.navLabel, path: page.path })),
  },
  {
    label: NAV_TOPIC_LABELS.data,
    path: '/data?section=connectors',
    children: [
      { label: 'Data connectors', path: '/data?section=connectors' },
      { label: 'Imported activity', path: '/data?section=operational' },
    ],
  },
  {
    label: NAV_TOPIC_LABELS.emissions,
    path: '/carbon-footprint?section=overview',
    children: [
      { label: 'Overview', path: '/carbon-footprint?section=overview' },
      { label: 'Footprint rows', path: '/carbon-footprint?section=operational' },
      { label: 'Analytics', path: '/carbon-footprint?section=carbon-analytics' },
      { label: 'Carbon picture', path: '/carbon-twin' },
      { label: 'Reduction ideas', path: '/carbon-footprint?section=recommendations' },
      { label: 'Forecasting', path: '/carbon-forecasting' },
    ],
  },
  {
    label: NAV_TOPIC_LABELS.compliance,
    path: '/compliance',
    children: [
      { label: 'Compliance', path: '/compliance' },
      { label: 'India market', path: complianceSectionHref('/compliance/india', 'overview') },
      { label: 'Global export', path: complianceSectionHref('/compliance/export', 'overview') },
      { label: 'Buyer audit pack', path: '/buyer-audit' },
      ...complianceReportingNavChildren(),
    ],
  },
  {
    label: NAV_TOPIC_LABELS.emissionsTargets,
    path: '/carbon-savings?section=savings',
    children: CARBON_SAVINGS_TRADING_SECTIONS.map((section) => ({
      label: section.label,
      path: section.path,
    })),
  },
];

export const SECONDARY_NAV_TOPICS: NavTopic[] = [
  { label: 'Information', path: '/information' },
  ...(SUBSCRIPTION_PLANS_ENABLED
    ? [{ label: 'Subscription & billing', path: '/billing' } as NavTopic]
    : []),
  { label: 'Data privacy', path: '/data-privacy' },
  { label: 'Settings', path: '/settings' },
  { label: 'Help center', path: '/help' },
];

export const SUPPORT_ROUTE_PREFIX = '/support';

export const isSupportRoute = (pathname: string): boolean =>
  pathname.startsWith(SUPPORT_ROUTE_PREFIX);

export const isPartnerPortalPath = (pathname: string): boolean =>
  pathname === '/partner/login' || pathname.startsWith('/partner/');

export const EXPERT_ROUTE_PREFIX = '/expert';

export const isExpertRoute = (pathname: string): boolean =>
  pathname.startsWith(EXPERT_ROUTE_PREFIX);

export const isInternalPortalPath = (pathname: string): boolean =>
  isAdminRoute(pathname) || isPartnerPortalPath(pathname) || isSupportRoute(pathname) || isExpertRoute(pathname);

/** Nav topics hidden until onboarding baseline is complete. */
export const ADVANCED_NAV_PATHS = new Set([
  '/compliance/export',
]);

export interface NavVisibilityInput {
  hasProfile: boolean;
  hasAssessment: boolean;
  hasExportIntent?: boolean;
}

export function filterNavTopicsByProgress(
  topics: NavTopic[],
  input: NavVisibilityInput
): NavTopic[] {
  let filtered = topics;

  // Progressive disclosure: keep the sidebar focused until baseline setup is done.
  if (!input.hasProfile) {
    filtered = filtered.filter(
      (topic) =>
        topic.label === NAV_TOPIC_LABELS.home
        || topic.label === NAV_TOPIC_LABELS.myCompany
    );
  } else if (!input.hasAssessment) {
    filtered = filtered
      .map((topic) => {
        // Keep Operations + Data connectors visible so tools are not buried.
        if (topic.label === NAV_TOPIC_LABELS.compliance && topic.children) {
          return {
            ...topic,
            children: topic.children.filter((child) => {
              if (child.path.includes('/compliance/export') || child.path === '/buyer-audit') {
                return input.hasExportIntent === true;
              }
              // Hub, India track, and disclosure reports — export stays intent-gated.
              return (
                child.path === '/compliance'
                || child.path.includes('/compliance/india')
                || child.path.includes('/reporting')
              );
            }),
          };
        }
        if (topic.label === NAV_TOPIC_LABELS.emissions && topic.children) {
          return {
            ...topic,
            children: topic.children.filter((child) => !child.path.includes('/carbon-forecasting')),
          };
        }
        if (topic.label === NAV_TOPIC_LABELS.emissionsTargets && topic.children) {
          return {
            ...topic,
            children: topic.children.filter((child) => !child.path.includes('section=trading')),
          };
        }
        return topic;
      })
      .filter((topic): topic is NavTopic => topic !== null);
  }

  if (SUBSCRIPTION_PLANS_ENABLED && !(input.hasProfile && input.hasAssessment)) {
    filtered = filtered
      .map((topic) => {
        if (topic.label === NAV_TOPIC_LABELS.compliance && topic.children) {
          return {
            ...topic,
            children: topic.children.filter((c) => !c.path.includes('/compliance/export')),
          };
        }
        return topic;
      });
  }

  if (!input.hasExportIntent) {
    filtered = filtered.map((topic) => {
      if (topic.label === NAV_TOPIC_LABELS.compliance && topic.children) {
        return {
          ...topic,
          children: topic.children.filter(
            (child) => !child.path.includes('/compliance/export') && child.path !== '/buyer-audit'
          ),
        };
      }
      return topic;
    });
  }

  return withoutBillUploadNav(filtered);
}

const isBillUploadNavChild = (child: NavSubPage): boolean => (
  child.path === '/document-upload' || child.path.includes('section=documents')
);

/** Show Upload bills only when the bill-upload flag is on. Otherwise keep connectors. */
export function withoutBillUploadNav(topics: NavTopic[]): NavTopic[] {
  const billUploadEnabled = isBillUploadEnabled();
  return topics.map((topic) => {
    const isData = topic.label === NAV_TOPIC_LABELS.data || topic.label === 'Data';
    if (!isData || !topic.children) {
      return topic;
    }
    const withoutBills = topic.children.filter((child) => !isBillUploadNavChild(child));
    if (!billUploadEnabled) {
      return {
        ...topic,
        path: withoutBills[0]?.path || '/data?section=connectors',
        children: withoutBills,
      };
    }
    if (topic.path.startsWith('/enterprise')) {
      return topic;
    }
    const children = withoutBills.some((child) => child.path === '/document-upload')
      ? withoutBills
      : [UPLOAD_BILLS_NAV_CHILD, ...withoutBills];
    return {
      ...topic,
      path: '/document-upload',
      children,
    };
  });
}

/** Authenticated routes that render without the left navigation pane. */
export const ROUTES_WITHOUT_SIDEBAR = new Set([
  '/',
  '/login',
  '/register',
  '/forgot-password',
  '/privacy-policy',
  '/terms-of-service',
  '/pricing',
  '/about-us',
  '/why-carbon-intelligence',
  '/why-sci',
  '/why-ci',
  '/partners',
  '/partners/api-documentation',
  '/insights',
  '/developers',
  '/partner/login',
  '/partner/dashboard',
  '/admin/login',
  '/support/dashboard',
  '/expert/dashboard',
  '/enterprise/registration',
  '/msme-registration',
]);

export const isEnterpriseAppPath = (pathname: string): boolean =>
  pathname.startsWith('/enterprise');

/** True for MSME workspace routes (not enterprise, admin, partner, support, or expert). */
export const isMsmeAppPath = (pathname: string): boolean =>
  !pathname.startsWith('/enterprise')
  && !pathname.startsWith('/admin')
  && !pathname.startsWith('/partner')
  && !pathname.startsWith('/support')
  && !pathname.startsWith('/expert');

/** Routes reachable before sign-in (header auth bypass). */
export const PUBLIC_ROUTES = new Set([...Array.from(ROUTES_WITHOUT_SIDEBAR), '/help', '/pricing', '/insights']);

export const isPublicRoute = (pathname: string): boolean => {
  if (PUBLIC_ROUTES.has(pathname)) {
    return true;
  }
  return pathname.startsWith('/insights/')
    || pathname.startsWith('/carbon-passport/')
    || pathname.startsWith('/supplier-invite/');
};

const isOperationsPath = (pathname: string) =>
  pathname === '/operations' || pathname.startsWith('/operations/');

export const ADMIN_ROUTE_PREFIX = '/admin';

export function isAdminRoute(pathname: string): boolean {
  return pathname.startsWith(ADMIN_ROUTE_PREFIX);
}

export function shouldShowAppSidebar(pathname: string, isRegistered: boolean): boolean {
  if (!isRegistered) {
    return false;
  }
  if (ROUTES_WITHOUT_SIDEBAR.has(pathname)) {
    return false;
  }
  if (isAdminRoute(pathname)) {
    return false;
  }
  if (isPartnerPortalPath(pathname)) {
    return false;
  }
  if (isSupportRoute(pathname)) {
    return false;
  }
  return true;
}

export function parseNavPath(path: string): { pathname: string; tab: number | null } {
  const [pathname, search = ''] = path.split('?');
  const params = new URLSearchParams(search);
  const tabParam = params.get('tab');
  const tab = tabParam !== null && tabParam !== '' ? Number(tabParam) : null;
  return { pathname, tab: Number.isFinite(tab) ? tab : null };
}

export function isNavPathActive(currentPathname: string, currentSearch: string, navPath: string): boolean {
  const url = new URL(navPath, 'http://local');
  const navPathname = url.pathname;
  const navTab = url.searchParams.get('tab');
  const navSection = url.searchParams.get('section');
  const currentParams = new URLSearchParams(currentSearch);

  if (currentPathname !== navPathname) {
    return false;
  }

  if (navSection !== null) {
    if (navPathname === '/compliance/india' || navPathname === '/compliance/export') {
      return currentPathname === navPathname && currentParams.get('section') === navSection;
    }
    return currentParams.get('section') === navSection;
  }

  if (navTab !== null) {
    if (currentParams.has('section')) {
      return false;
    }
    const currentTab = currentParams.get('tab');
    if (navTab === '0' && (currentTab === null || currentTab === '')) {
      return true;
    }
    return currentTab === navTab;
  }

  // Page-level nav entries (no tab/section in the href) stay active for any
  // ?tab= / ?section= on the same pathname so breadcrumbs keep the current page
  // (DEEP-BUG-008 / DEEP-BUG-012).
  return true;
}

export function isTopicActive(
  currentPathname: string,
  currentSearch: string,
  topic: NavTopic
): boolean {
  const { pathname: topicPathname } = parseNavPath(topic.path);
  if (topicPathname === '/msme-profile' && isOperationsPath(currentPathname)) {
    return true;
  }
  if (topicPathname === '/compliance') {
    if (
      currentPathname === '/compliance'
      || currentPathname.startsWith('/compliance/')
      || currentPathname === '/reporting'
      || currentPathname === '/buyer-audit'
    ) {
      return true;
    }
  }
  if (topicPathname === '/carbon-savings') {
    if (
      currentPathname === '/carbon-savings'
      || currentPathname === '/emissions-reduction-targets'
      || currentPathname === '/carbon-marketplace'
      || currentPathname.startsWith('/carbon-credits')
      || currentPathname.startsWith('/carbon-trading')
    ) {
      return true;
    }
  }
  if (topicPathname === '/data') {
    if (currentPathname === '/document-upload') {
      return true;
    }
  }
  if (currentPathname === topicPathname || currentPathname.startsWith(`${topicPathname}/`)) {
    return true;
  }
  return (
    topic.children?.some((child) => isNavPathActive(currentPathname, currentSearch, child.path)) ??
    false
  );
}
